// Searchable select popover — FE-043b parity v3.
//
// Used by the detail-page assignee / reviewer rows, the tag and
// watcher chip pickers, and the list-row assignee column. Mirrors
// the ClickUp model: click the trigger → popover opens with a
// search input + filtered list. Picking saves immediately
// (single-select) or toggles inclusion (multi-select). No
// separate save button — one click is enough.
//
// The portal-rendered popover is anchored to the trigger via
// fixed positioning so the kanban board's overflow-x-auto can't
// clip it (same lesson as the Move-to menu).

import { Check, Search, X } from "lucide-react";
import {
  useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import { createPortal } from "react-dom";


export interface SearchableOption {
  value: string;
  label: string;
  /** Optional secondary line shown beneath the label (email,
   * commitment number, etc.). */
  hint?: string;
  /** Optional left-aligned content (avatar bubble, color dot). */
  leading?: ReactNode;
  /** Optional right-aligned trailing content. */
  trailing?: ReactNode;
  /** When true, render this option as a "clear" affordance and
   * fire onPick("") on click — used to model "Unassigned" in
   * single-select mode. */
  isClear?: boolean;
}


type SearchableSelectBaseProps = {
  /** Stable id used for testid namespacing. */
  fieldId: string;
  options: SearchableOption[];
  placeholder?: string;
  /** Placeholder shown by the trigger when no value is set. */
  emptyDisplay?: ReactNode;
  /** Caller-supplied trigger render — usually the value display.
   * Wrapped by the picker in a button. */
  triggerDisplay: ReactNode;
  disabled?: boolean;
  isPending?: boolean;
};


export type SearchableSelectProps =
  | (SearchableSelectBaseProps & {
      multiple?: false;
      value: string;
      onPick: (next: string) => void | Promise<void>;
    })
  | (SearchableSelectBaseProps & {
      multiple: true;
      value: string[];
      onPick: (next: string) => void | Promise<void>;
      onUnpick: (next: string) => void | Promise<void>;
    });


const POPOVER_WIDTH = 280;
const POPOVER_GAP = 4;
const POPOVER_MAX_HEIGHT = 320;


export function SearchableSelect(props: SearchableSelectProps) {
  const {
    fieldId, options, placeholder, triggerDisplay,
    disabled, isPending,
  } = props;
  const [open, setOpen] = useState(false);
  const [needle, setNeedle] = useState("");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; flipUp: boolean } | null>(null);

  // Close + reset search on close.
  useEffect(() => {
    if (!open) {
      setNeedle("");
      return;
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  // Position the popover on open.
  useLayoutEffect(() => {
    if (!open) return;
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const flipUp =
      r.bottom + POPOVER_MAX_HEIGHT + POPOVER_GAP > window.innerHeight
      && r.top > POPOVER_MAX_HEIGHT + POPOVER_GAP;
    const desiredLeft = r.left;
    const left = Math.max(
      8,
      Math.min(window.innerWidth - POPOVER_WIDTH - 8, desiredLeft),
    );
    setPos({
      top: flipUp ? r.top - POPOVER_GAP : r.bottom + POPOVER_GAP,
      left,
      flipUp,
    });
  }, [open]);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    function md(e: MouseEvent) {
      if (!(e.target instanceof Node)) return;
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function kd(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", md, true);
    document.addEventListener("keydown", kd);
    return () => {
      document.removeEventListener("mousedown", md, true);
      document.removeEventListener("keydown", kd);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = needle.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q)
        || (o.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [options, needle]);

  const selectedSet = props.multiple
    ? new Set(props.value)
    : new Set([props.value]);

  function pickOption(opt: SearchableOption): void {
    if (props.multiple) {
      const isSelected = selectedSet.has(opt.value);
      if (opt.isClear) {
        // Multi-select with a clear option doesn't make sense;
        // ignore.
        setOpen(false);
        return;
      }
      // Fire-and-forget: the consumer's onPick / onUnpick may return
      // a rejecting promise to signal a backend error to its own
      // mutation onError handler (which updates field state). The
      // picker doesn't render that error itself, so the rejection
      // would otherwise propagate as unhandled. Swallow it here.
      if (isSelected) {
        Promise.resolve(props.onUnpick(opt.value)).catch(() => {});
      } else {
        Promise.resolve(props.onPick(opt.value)).catch(() => {});
      }
      // Keep open so the user can pick more.
      return;
    }
    const next = opt.isClear ? "" : opt.value;
    Promise.resolve(props.onPick(next)).catch(() => {});
    setOpen(false);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`searchable-${fieldId}-trigger`}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1 text-left text-sm text-text-primary hover:bg-recessed/60 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
          {triggerDisplay}
        </span>
        {isPending && (
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 animate-pulse rounded-full bg-teal"
          />
        )}
      </button>
      {open && pos && createPortal(
        <div
          ref={popoverRef}
          role="listbox"
          data-testid={`searchable-${fieldId}-popover`}
          style={{
            position: "fixed",
            top: pos.flipUp ? "auto" : pos.top,
            bottom: pos.flipUp
              ? window.innerHeight - pos.top
              : "auto",
            left: pos.left,
            width: POPOVER_WIDTH,
            maxHeight: POPOVER_MAX_HEIGHT,
          }}
          className="z-[100] flex flex-col overflow-hidden rounded-md border border-border-subtle bg-raised shadow-lg"
        >
          <label className="relative flex items-center border-b border-border-subtle">
            <Search
              size={12} aria-hidden="true"
              className="pointer-events-none absolute left-2.5 text-text-tertiary"
            />
            <input
              ref={inputRef}
              type="search"
              data-testid={`searchable-${fieldId}-search`}
              value={needle}
              onChange={(e) => setNeedle(e.target.value)}
              placeholder={placeholder ?? "Search…"}
              className="w-full bg-transparent py-2 pl-7 pr-3 text-xs text-text-primary outline-none"
            />
          </label>
          <ul role="presentation" className="flex flex-col overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li
                data-testid={`searchable-${fieldId}-empty`}
                className="px-3 py-2 text-center text-xs text-text-tertiary"
              >
                No matches.
              </li>
            ) : (
              filtered.map((opt) => {
                const isSelected = selectedSet.has(opt.value);
                return (
                  <li key={opt.value || `__clear_${opt.label}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-testid={`searchable-${fieldId}-option-${opt.value || "clear"}`}
                      onClick={() => pickOption(opt)}
                      className={[
                        "flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-recessed",
                        opt.isClear ? "text-text-tertiary italic" : "text-text-primary",
                      ].join(" ")}
                    >
                      {opt.leading}
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{opt.label}</span>
                        {opt.hint && (
                          <span className="truncate text-[10px] text-text-tertiary">
                            {opt.hint}
                          </span>
                        )}
                      </span>
                      {props.multiple && isSelected && (
                        <Check size={12} aria-hidden="true" className="text-teal" />
                      )}
                      {opt.trailing}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {props.multiple && (
            <footer className="flex items-center justify-end border-t border-border-subtle px-2 py-1">
              <button
                type="button"
                data-testid={`searchable-${fieldId}-done`}
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1 text-xs font-medium text-teal hover:underline"
              >
                Done
                <X size={10} aria-hidden="true" />
              </button>
            </footer>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
